import { isString } from 'lodash';

import {
  getSharedAuthStorageItem,
  JOIN_WORKSPACE_INFO_KEY,
  removeSharedAuthStorageItem,
  setSharedAuthStorageItem,
} from '~/utils/auth/SessionHelpers';
import { sanitizeEmail } from '~/utils/EmailUtils';

export enum InviteType {
  email = 'email',
  link = 'link',
}

export type StoredJoinInfo = {
  id: string;
  name: string;
  image: string;
  email?: string;
  shortId: string;
  inviteType: InviteType;
  /** Email that must authenticate before this invite can be consumed. */
  boundEmail?: string;
};

const isJoinInfo = (value: unknown): value is StoredJoinInfo => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return (
    'id' in value &&
    typeof value.id === 'string' &&
    'name' in value &&
    typeof value.name === 'string' &&
    'image' in value &&
    typeof value.image === 'string' &&
    'shortId' in value &&
    typeof value.shortId === 'string' &&
    'inviteType' in value &&
    (value.inviteType === InviteType.email || value.inviteType === InviteType.link) &&
    (!('email' in value) || typeof value.email === 'string') &&
    (!('boundEmail' in value) || typeof value.boundEmail === 'string')
  );
};

const parseStoredJoinInfo = (raw: string | null): StoredJoinInfo | undefined => {
  if (!isString(raw)) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    return isJoinInfo(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const readJoinInfo = (): StoredJoinInfo | undefined =>
  parseStoredJoinInfo(getSharedAuthStorageItem(JOIN_WORKSPACE_INFO_KEY));

const writeJoinInfo = (info: StoredJoinInfo): void => {
  setSharedAuthStorageItem(JOIN_WORKSPACE_INFO_KEY, JSON.stringify(info));
};

const normalizeBoundEmail = (email?: string): string | undefined => {
  const normalized = sanitizeEmail(email);

  return normalized || undefined;
};

const withBoundEmail = (info: StoredJoinInfo, boundEmail?: string): StoredJoinInfo => {
  const normalizedBoundEmail = normalizeBoundEmail(boundEmail ?? info.boundEmail ?? info.email);

  if (!normalizedBoundEmail) {
    return info;
  }

  return {
    ...info,
    boundEmail: normalizedBoundEmail,
  };
};

/** Returns any stored invite for display-only surfaces (e.g. mismatch redirect page). */
export const peekJoinWorkspaceInfo = (): StoredJoinInfo | undefined => readJoinInfo();

export const getJoinWorkspaceInfoForEmail = (email: string): StoredJoinInfo | undefined => {
  const normalizedEmail = normalizeBoundEmail(email);

  if (!normalizedEmail) {
    return undefined;
  }

  const info = readJoinInfo();

  if (!info) {
    return undefined;
  }

  const boundEmail = normalizeBoundEmail(info.boundEmail ?? info.email);

  if (boundEmail && boundEmail === normalizedEmail) {
    return info;
  }

  return undefined;
};

export const setJoinWorkspaceInfo = (info: StoredJoinInfo): void => {
  if (typeof window === 'undefined') {
    return;
  }

  writeJoinInfo(withBoundEmail(info));
};

/** Attach the authenticating email so post-auth and verify tabs can consume the invite. */
export const bindJoinWorkspaceInfoToEmail = (email: string): void => {
  const normalizedEmail = normalizeBoundEmail(email);

  if (!normalizedEmail || typeof window === 'undefined') {
    return;
  }

  const info = readJoinInfo();

  if (!info) {
    return;
  }

  const existingBoundEmail = normalizeBoundEmail(info.boundEmail ?? info.email);

  if (existingBoundEmail && existingBoundEmail !== normalizedEmail) {
    return;
  }

  writeJoinInfo({
    ...info,
    boundEmail: normalizedEmail,
  });
};

export const clearJoinWorkspaceInfo = (): void => {
  removeSharedAuthStorageItem(JOIN_WORKSPACE_INFO_KEY);
};
